import React, { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ListScrollOverlayIndicator } from 'src/components/general/feedback/list-scroll-overlay-indicator';
import { Icon } from 'src/components/general/icon';

const COLLAPSED_ITEMS_COUNT = 3;
const VIEW_ALL_THRESHOLD = 5;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  line-height: 22px;

  p {
    margin: 0;
  }
`;

const Intro = styled.p`
  margin: 0;
`;

const ItemsScroll = styled(ListScrollOverlayIndicator)`
  margin-top: 10px;

  &::after {
    border-radius: 0;
    height: 57px;
  }
`;

const ItemsList = styled.ul<{ $expanded: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  list-style: none;
  margin: 0;
  max-height: ${({ $expanded }) => ($expanded ? '244px' : 'none')};
  overflow-y: ${({ $expanded }) => ($expanded ? 'auto' : 'visible')};
  padding: 0;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.flint}80;
    border-radius: 3px;
  }
`;

const Item = styled.li`
  align-items: center;
  color: ${({ theme }) => theme.colors.smalt};
  display: flex;
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 13px;
  gap: 6px;
  line-height: 20px;

  &::before {
    background: radial-gradient(
        circle,
        ${({ theme }) => theme.colors.gogo} 2.5px,
        transparent 2.5px
      ),
      ${({ theme }) => theme.colors.porcelain2};
    border-radius: 3px;
    content: '';
    flex-shrink: 0;
    height: 16px;
    width: 16px;
  }
`;

const ItemsToggle = styled.button<{ $expanded: boolean }>`
  align-items: center;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.gogo};
  cursor: pointer;
  display: flex;
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 13px;
  gap: 6px;
  line-height: 20px;
  margin-top: 8px;
  padding: 0;

  svg {
    height: 10px;
    transform: rotate(${({ $expanded }) => ($expanded ? '180deg' : '-90deg')});
    width: 10px;

    path {
      fill: ${({ theme }) => theme.colors.gogo};
    }
  }
`;

const ToggleIcon = styled.span`
  align-items: center;
  display: flex;
  height: 16px;
  justify-content: center;
  width: 16px;
`;

export const InUseItemsList = ({
  testId,
  intro,
  items,
  children,
}: {
  testId: string;
  intro: string;
  items: { id: string; title: string }[];
  children: ReactNode;
}) => {
  const { t } = useTranslation(['aiAgents']);
  const [expanded, setExpanded] = useState(false);
  const [hasHiddenItems, setHasHiddenItems] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  const isCollapsible = items.length >= VIEW_ALL_THRESHOLD;
  const visibleItems =
    isCollapsible && !expanded ? items.slice(0, COLLAPSED_ITEMS_COUNT) : items;

  const updateHiddenItems = () => {
    const list = listRef.current;

    setHasHiddenItems(
      !!list && list.scrollTop + list.clientHeight < list.scrollHeight - 1,
    );
  };

  useLayoutEffect(updateHiddenItems, [expanded, items]);

  return (
    <Container data-lgg-id={testId}>
      <div>
        <Intro>{intro}</Intro>
        <ItemsScroll $showBottomShadow={hasHiddenItems}>
          <ItemsList ref={listRef} $expanded={expanded} onScroll={updateHiddenItems}>
            {visibleItems.map(({ id, title }) => (
              <Item key={id} data-lgg-id={`${testId}-item`}>
                {title}
              </Item>
            ))}
          </ItemsList>
        </ItemsScroll>
        {isCollapsible && (
          <ItemsToggle
            type="button"
            $expanded={expanded}
            data-lgg-id={`${testId}-toggle`}
            onClick={() => setExpanded(!expanded)}
          >
            <ToggleIcon>
              <Icon type="chevronDown" />
            </ToggleIcon>
            {expanded
              ? t('aiAgents:knowledge.inUseItemsList.viewLess')
              : t('aiAgents:knowledge.inUseItemsList.viewAll', { count: items.length })}
          </ItemsToggle>
        )}
      </div>
      {children}
    </Container>
  );
};
